import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });
  const { t } = useLanguage();

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('cookie-consent', JSON.stringify(onlyNecessary));
    setIsVisible(false);
  };

  const cookieOptions = [
    {
      key: 'necessary',
      title: t.cookie.necessary,
      description: t.cookie.necessaryDesc,
      disabled: true,
    },
    {
      key: 'functional',
      title: t.cookie.functional,
      description: t.cookie.functionalDesc,
      disabled: false,
    },
    {
      key: 'analytics',
      title: t.cookie.analytics,
      description: t.cookie.analyticsDesc,
      disabled: false,
    },
    {
      key: 'marketing',
      title: t.cookie.marketing,
      description: t.cookie.marketingDesc,
      disabled: false,
    },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="glass rounded-2xl p-6 md:p-8 border border-white/10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <Cookie className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">{t.cookie.title}</h3>
                </div>
                <button
                  onClick={() => setIsVisible(false)}
                  className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>

              {!showSettings ? (
                <>
                  <p className="text-white/70 text-sm mb-6 leading-relaxed">
                    {t.cookie.description}{' '}
                    <Link to="/datenschutz" className="text-[#D4AF37] hover:underline">
                      {t.cookie.learnMore}
                    </Link>
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="flex-1 bg-[#D4AF37] text-black py-3 px-6 rounded-full font-semibold text-sm hover:bg-[#E5C34A] transition-all gold-glow"
                      data-testid="cookie-accept-all"
                    >
                      {t.cookie.acceptAll}
                    </button>
                    <button
                      onClick={handleRejectAll}
                      className="flex-1 bg-transparent border border-white/20 text-white py-3 px-6 rounded-full font-medium text-sm hover:border-white/40 transition-all"
                      data-testid="cookie-reject-all"
                    >
                      {t.cookie.rejectAll}
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="flex items-center justify-center gap-2 px-6 py-3 text-white/70 hover:text-[#D4AF37] transition-colors text-sm"
                      data-testid="cookie-settings-btn"
                    >
                      <Settings className="w-4 h-4" />
                      <span>{t.cookie.settings}</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cookieOptions.map((cookie) => (
                      <div
                        key={cookie.key}
                        className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white/5"
                      >
                        <div className="flex-1">
                          <h4 className="text-white text-sm font-medium">{cookie.title}</h4>
                          <p className="text-white/50 text-xs mt-1">{cookie.description}</p>
                        </div>
                        <button
                          onClick={() =>
                            !cookie.disabled &&
                            setPreferences((prev) => ({
                              ...prev,
                              [cookie.key]: !prev[cookie.key],
                            }))
                          }
                          disabled={cookie.disabled}
                          className={`w-12 h-6 rounded-full transition-colors flex items-center px-1 ${
                            preferences[cookie.key]
                              ? 'bg-[#D4AF37]'
                              : 'bg-white/20'
                          } ${cookie.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          data-testid={`cookie-toggle-${cookie.key}`}
                        >
                          <div
                            className={`w-4 h-4 rounded-full bg-white transition-transform ${
                              preferences[cookie.key] ? 'translate-x-6' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleAcceptSelected}
                      className="flex-1 bg-[#D4AF37] text-black py-3 px-6 rounded-full font-semibold text-sm hover:bg-[#E5C34A] transition-all gold-glow flex items-center justify-center gap-2"
                      data-testid="cookie-save-settings"
                    >
                      <Check className="w-4 h-4" />
                      {t.cookie.saveSettings}
                    </button>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="px-6 py-3 text-white/70 hover:text-white transition-colors text-sm"
                    >
                      {t.cookie.back}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
